import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ListFilterToolbar from 'src/common/components/list-filters/ListFilterToolbar';
import { SponsorsPublishedFilter, SponsorsSortBy } from 'src/helpers/filterSponsors';
import { hasActiveSelectFilter } from 'src/helpers/listFilters';
import { EView } from 'src/interfaces/View';
import useSponsorsStore from 'src/stores/sponsorsStore';

interface SponsorsToolbarProps {
  view: EView;
  onSetView: (view: EView) => void;
}

const SponsorsToolbar = ({ view, onSetView }: SponsorsToolbarProps) => {
  const { t } = useTranslation();
  const {
    search,
    publishedFilter,
    sortBy,
    sortOrder,
    setSearch,
    setPublishedFilter,
    setSortBy,
    setSortOrder,
  } = useSponsorsStore();

  const sortOptions = useMemo(
    () => [
      { label: t('system.sponsors.sort.displayOrder'), value: 'sortOrder' },
      { label: t('system.sponsors.name'), value: 'name' },
      { label: t('system.fileManager.sort.date'), value: 'createdAt' },
    ],
    [t],
  );

  return (
    <ListFilterToolbar
      popupId="sponsorsFilters"
      searchQuery={search}
      onSearchChange={setSearch}
      searchPlaceholder={t('system.listFilters.searchPlaceholder')}
      hasActiveFilters={hasActiveSelectFilter(publishedFilter)}
      filterPopoverContent={
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid size={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="sponsors-published-filter-label" shrink>
                {t('system.sponsors.publishedStatus')}
              </InputLabel>
              <Select
                labelId="sponsors-published-filter-label"
                value={publishedFilter}
                label={t('system.sponsors.publishedStatus')}
                onChange={(e: SelectChangeEvent<SponsorsPublishedFilter>) =>
                  setPublishedFilter(e.target.value as SponsorsPublishedFilter)
                }
              >
                <MenuItem value="all">{t('base.all')}</MenuItem>
                <MenuItem value="true">{t('system.sponsors.published')}</MenuItem>
                <MenuItem value="false">{t('system.sponsors.unpublished')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      }
      order={sortOrder}
      orderByKey={sortBy}
      setOrder={setSortOrder}
      setOrderByKey={key => setSortBy(key as SponsorsSortBy)}
      sortLabel={t('sorter.order')}
      sortOptions={sortOptions}
      view={view}
      onSetView={onSetView}
    />
  );
};

export default SponsorsToolbar;
